/**
 * Shared pieces of the BI chart types (D529): the selection contract (the cross-filter reads it), the export toolbar, the
 * 2-D active-cell state for grids, the target mark and the heat helpers.
 *
 * The selection contract is `onSelect({ dimension, value })`: `dimension` is the id of the plotted dimension (the
 * `dimension` prop of the chart) and `value` the key of the mark the user clicked or activated with Enter / Space. A chart with two
 * dimensions (a heat grid) names both (`rowDimension`, `colDimension`) and reports each one in turn. A chart never decides
 * what a selection does: the screen owns the filter.
 */
import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactElement } from 'react';
import { clamp, roundCoord, type LinearScale } from '@vello/charts-core';
import { ChartExportMenu, type ChartCsv, type ChartExportProps } from './export';
import { ChartEmpty, ChartLoading, cx } from './kit';
import type { ChartBaseProps } from './line-chart';

// ── Selection ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** What a chart reports when a mark is chosen (click, tap, Enter or Space on the focused chart). */
export interface ChartSelection {
  readonly dimension: string;
  readonly value: string;
}

export interface ChartSelectProps {
  /** The id of the dimension the marks stand for (e.g. `weekday`); sent back in `onSelect`. */
  readonly dimension?: string;
  /** Called with `{ dimension, value }` when the user picks a mark. Marks become keyboard-selectable (Enter / Space on the active one). */
  readonly onSelect?: (selection: ChartSelection) => void;
  /** The values currently chosen (the active cross-filter): the other marks are quieter and the chosen ones carry an outline, so it is not colour alone. */
  readonly selected?: readonly string[];
}

/** The dimension id used when a chart is made selectable without naming one. */
export const DEFAULT_DIMENSION = 'category';

/** Report a selection if the chart is selectable. */
export function emitSelect(props: ChartSelectProps, value: string, dimension?: string): void {
  props.onSelect?.({ dimension: dimension ?? props.dimension ?? DEFAULT_DIMENSION, value });
}

/** Whether `value` is one of the chosen ones. */
export function isChosen(props: ChartSelectProps, value: string): boolean {
  return props.selected?.includes(value) ?? false;
}

/** Whether any value is chosen (the unchosen marks then recede). */
export function hasChosen(props: ChartSelectProps): boolean {
  return (props.selected?.length ?? 0) > 0;
}

/** The data attributes of a mark that may be chosen: `data-chosen` is `true`/`false` only while a selection exists. */
export function chosenAttr(props: ChartSelectProps, value: string): { 'data-chosen'?: 'true' | 'false'; 'data-selectable'?: 'true' } {
  return {
    ...(hasChosen(props) ? { 'data-chosen': isChosen(props, value) ? ('true' as const) : ('false' as const) } : {}),
    ...(props.onSelect !== undefined ? { 'data-selectable': 'true' as const } : {}),
  };
}

// ── Toolbar (export) ───────────────────────────────────────────────────────────────────────────────────────────────

/** The export toolbar row of a chart; nothing when the caller did not opt in with `exportLabels`. */
export function ChartToolbar({ props, title, csv, png = true }: { props: ChartExportProps; title?: string | undefined; csv: () => ChartCsv; png?: boolean }): ReactElement | null {
  if (props.exportLabels === undefined) return null;
  return (
    <div className="vello-chart__toolbar">
      <ChartExportMenu labels={props.exportLabels} name={props.exportName ?? title ?? 'chart'} csv={csv} png={png} />
    </div>
  );
}

// ── 2-D active cell ────────────────────────────────────────────────────────────────────────────────────────────────

export interface CellRef {
  readonly row: number;
  readonly col: number;
}

export interface ActiveCell {
  readonly cell: CellRef | null;
  readonly set: (cell: CellRef | null) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  readonly onFocus: () => void;
  readonly onBlur: () => void;
  readonly onPointerLeave: () => void;
  /** Mark the interaction as a pointer one (a press that focuses must not open the first cell). */
  readonly markPointer: () => void;
}

/**
 * The active cell of a grid. Arrow keys move by one cell (Home / End: first / last column of the row; PageUp / PageDown:
 * first / last row), Escape clears, Enter and Space activate. On keyboard focus the first cell with data opens.
 */
export function useActiveCell(rows: number, cols: number, onActivate?: (cell: CellRef) => void, firstCell: CellRef = { row: 0, col: 0 }): ActiveCell {
  const [raw, setRaw] = useState<CellRef | null>(null);
  const pointer = useRef(false);
  const cell = raw !== null && raw.row >= 0 && raw.row < rows && raw.col >= 0 && raw.col < cols ? raw : null;
  const set = useCallback((next: CellRef | null) => setRaw(next), []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      if (rows === 0 || cols === 0) return;
      const at = cell ?? firstCell;
      let next: CellRef | null | undefined;
      if (event.key === 'ArrowRight') next = { row: at.row, col: Math.min(cols - 1, at.col + 1) };
      else if (event.key === 'ArrowLeft') next = { row: at.row, col: Math.max(0, at.col - 1) };
      else if (event.key === 'ArrowDown') next = { row: Math.min(rows - 1, at.row + 1), col: at.col };
      else if (event.key === 'ArrowUp') next = { row: Math.max(0, at.row - 1), col: at.col };
      else if (event.key === 'Home') next = { row: at.row, col: 0 };
      else if (event.key === 'End') next = { row: at.row, col: cols - 1 };
      else if (event.key === 'PageUp') next = { row: 0, col: at.col };
      else if (event.key === 'PageDown') next = { row: rows - 1, col: at.col };
      else if (event.key === 'Escape') next = null;
      else if ((event.key === 'Enter' || event.key === ' ') && cell !== null && onActivate !== undefined) {
        event.preventDefault();
        onActivate(cell);
        return;
      }
      if (next === undefined) return;
      event.preventDefault();
      setRaw(next);
    },
    [rows, cols, cell, onActivate, firstCell],
  );

  return {
    cell,
    set,
    onKeyDown,
    onFocus: () => {
      if (!pointer.current) setRaw((prev) => prev ?? (rows > 0 && cols > 0 ? firstCell : null));
    },
    onBlur: () => setRaw(null),
    onPointerLeave: () => {
      pointer.current = false;
      setRaw(null);
    },
    markPointer: () => {
      pointer.current = true;
    },
  };
}

/** The element-relative position of a pointer event. */
export function pointerPosition(event: PointerEvent<Element> | { clientX: number; clientY: number; currentTarget: Element }): { x: number; y: number } {
  const rect = event.currentTarget.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

// ── Heat helpers ───────────────────────────────────────────────────────────────────────────────────────────────────

/** The heat steps the token scale carries. */
export const HEAT_LEVELS = 5;

/** The fill of a heat level (1 … 5); a cell with no data uses the quiet track. */
export function heatFill(level: number | null): string {
  return level === null ? 'var(--chart-track)' : `var(--chart-heat-${clamp(Math.round(level), 1, HEAT_LEVELS)})`;
}

/** The text colour that reads on a heat level (the audit holds each pairing to AA). */
export function heatInk(level: number | null): string {
  return level !== null && level >= 4 ? 'var(--chart-heat-ink-high)' : 'var(--chart-heat-ink-low)';
}

/** The low → high ramp under a heat chart: five swatches with the end values printed (the numbers carry the meaning, not the ramp). */
export function HeatLegend({ min, max, className }: { min: string; max: string; className?: string }): ReactElement {
  return (
    <div className={cx('vello-chart__heat-legend', className)} aria-hidden="true">
      <span className="tnum">{min}</span>
      <span className="vello-chart__heat-ramp">
        {Array.from({ length: HEAT_LEVELS }, (_, i) => (
          <span key={i} className="vello-chart__heat-step" style={{ background: heatFill(i + 1) }} />
        ))}
      </span>
      <span className="tnum">{max}</span>
    </div>
  );
}

// ── Target ─────────────────────────────────────────────────────────────────────────────────────────────────────────

/** A goal line (and, optionally, an accepted band) on a value axis. */
export interface ChartTarget {
  readonly value: number;
  /** The goal's name, printed at the line and added to the accessible summary (localized, injected). */
  readonly label: string;
  /** An accepted range around the goal, shaded quietly. */
  readonly band?: readonly [number, number];
}

/** The values a target adds to the y domain, so the line is never clipped. */
export function targetExtent(target: ChartTarget | undefined): number[] {
  if (target === undefined) return [];
  return [target.value, ...(target.band ?? [])];
}

/**
 * The target mark in a plot: the shaded band behind the series (drawn first by the caller) and the dashed line with a
 * card-coloured halo, so it reads over a column as well as over the white. Colour is not the only signal: the line is
 * dashed and its label is printed at the right end.
 */
export function TargetMark({ target, y, left, right, format, layer }: { target: ChartTarget; y: LinearScale; left: number; right: number; format: (value: number) => string; layer: 'band' | 'line' }): ReactElement | null {
  if (layer === 'band') {
    if (target.band === undefined) return null;
    const top = y(Math.max(target.band[0], target.band[1]));
    const bottom = y(Math.min(target.band[0], target.band[1]));
    return <rect className="vello-chart__target-band" x={left} y={roundCoord(top)} width={Math.max(0, right - left)} height={Math.max(0, roundCoord(bottom - top))} />;
  }
  const ly = roundCoord(y(target.value));
  return (
    <g className="vello-chart__target">
      <line className="vello-chart__target-halo" x1={left} x2={right} y1={ly} y2={ly} />
      <line className="vello-chart__target-line" x1={left} x2={right} y1={ly} y2={ly} />
      <text className="vello-chart__target-label tnum" x={right} y={ly - 5} textAnchor="end">
        {`${target.label} ${format(target.value)}`}
      </text>
    </g>
  );
}

/** The phrase added to a summary for a target (`label: value`). */
export function targetSummary(target: ChartTarget | undefined, format: (value: number) => string): string | undefined {
  return target === undefined ? undefined : `${target.label}: ${format(target.value)}`;
}

// ── Shared frame pieces ────────────────────────────────────────────────────────────────────────────────────────────

/** Width of one mono tick-label character at the micro size, in SVG units (the same estimate the base charts use). */
export const AXIS_CHAR_WIDTH = 6.8;

/** The loading or empty placeholder of a chart, or `null` when it has data to draw. */
export function chartState(props: ChartBaseProps, height: number, hasData: boolean): ReactElement | null {
  if (props.loading === true) return <ChartLoading height={height} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  if (!hasData) return <ChartEmpty height={height} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return null;
}

export interface FrameMetrics {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
  readonly plotWidth: number;
  readonly plotHeight: number;
  readonly baselineY: number;
}

/** The plot rectangle inside the SVG once the value axis text (left, and optionally right) and the x axis are reserved. */
export function frameMetrics(args: { width: number; height: number; tickText: readonly string[]; rightTickText?: readonly string[]; showAxis: boolean; xAxisHeight?: number; top?: number; rightPad?: number }): FrameMetrics {
  const top = args.top ?? 16;
  const longest = (texts: readonly string[]): number => texts.reduce((m, t) => Math.max(m, t.length), 0);
  const left = args.showAxis ? clamp(longest(args.tickText) * AXIS_CHAR_WIDTH + 14, 30, 96) : 8;
  const right = args.rightTickText !== undefined && args.showAxis ? clamp(longest(args.rightTickText) * AXIS_CHAR_WIDTH + 14, 30, 96) : (args.rightPad ?? 8);
  const bottom = args.showAxis ? (args.xAxisHeight ?? 26) : 8;
  const plotWidth = Math.max(args.width - left - right, 20);
  const plotHeight = Math.max(args.height - top - bottom, 20);
  return { left, right, top, bottom, plotWidth, plotHeight, baselineY: top + plotHeight };
}

/** Shorten a label to about `maxChars` characters with an ellipsis (the full text stays in the tooltip and the summary). */
export function truncate(text: string, maxChars: number): string {
  const max = Math.max(1, Math.floor(maxChars));
  return text.length <= max ? text : `${text.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

/** The default share formatter: a whole percent. */
export function formatPercentDefault(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}
